import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URLS } from '../constants';
import { authenticatedFetch } from '../utils/api';
import { useAuth, useLanguage } from '@/contexts';

const UpdateEmailPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [newEmail, setNewEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Redirect SSO users back to settings
  useEffect(() => {
    if (user && (user.isSSOOnly || user.hasSSO)) {
      navigate('/settings');
    }
  }, [user, navigate]);

  const handleSendVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newEmail) {
      setMessage({ type: 'error', text: t('pleaseEnterNewEmailAddress') });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const response = await authenticatedFetch(API_URLS.EMAIL_UPDATE.SEND_VERIFICATION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newEmail })
      });

      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: t('verificationCodeSent') });
        
        // In development, show the code for testing
        if (data.debug?.code) {
          setMessage({ 
            type: 'success', 
            text: `Verification code sent! (Dev mode: ${data.debug.code})` 
          });
        }
        
        // Navigate to verification page
        navigate('/verify-email-update', { 
          state: { newEmail, code: data.debug?.code } 
        });
      } else {
        const errorData = await response.json();
        // Handle both old and new error response formats
        const errorMessage = errorData.error?.message || errorData.message || t('failedSendVerificationCode');
        setMessage({ type: 'error', text: errorMessage });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('failedSendVerificationCode') });
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render for SSO users
  if (user && (user.isSSOOnly || user.hasSSO)) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">{t('updateEmailAddress')}</h1>
            <p className="text-gray-600">
              {t('updateEmailAddressDescription')}
            </p>
          </div>

          {message && (
            <div className={`p-4 rounded-md mb-6 ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSendVerification} className="space-y-4">
            <div>
              <label htmlFor="newEmail" className="block text-sm font-medium text-gray-700 mb-1">
                {t('newEmailAddressPlaceholder')}
              </label>
              <input
                type="email"
                id="newEmail"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-electric-blue focus:border-brand-electric-blue"
                placeholder={t('newEmailAddress')}
                required
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-brand-dark-blue text-white text-sm font-medium rounded-md hover:bg-brand-electric-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? t('sending') : t('sendVerificationCode')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/settings')}
                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateEmailPage;
